import { useState } from 'react'

import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { captureException } from '~/utils/errors'

const MAX_RETRIES = 2

type UseQueryWrapperOptions = Pick<
  UseQueryOptions,
  | 'queryKey'
  | 'queryFn'
  | 'refetchInterval'
  | 'enabled'
  | 'initialData'
  | 'placeholderData'
>

export function useQueryWrapper<T>(options: UseQueryWrapperOptions) {
  const {
    queryKey,
    queryFn,
    refetchInterval,
    enabled,
    initialData,
    placeholderData,
  } = options
  const [disabledOverride, setDisabledOverride] = useState(false)

  return useQuery({
    queryKey,
    queryFn,
    refetchInterval,
    // `?? true` so an omitted `enabled` doesn't make this `undefined`, which
    // react-query treats as enabled, discarding disabledOverride.
    enabled: (enabled ?? true) && !disabledOverride,
    retryOnMount: false,
    retry: (failureCount, error) => {
      if (isAxiosError(error)) {
        const statusCode = error?.response?.status
        if (statusCode && statusCode >= 400 && statusCode < 500) {
          // Never retry a 4xx error
          setDisabledOverride(true)
          captureException(
            new Error(
              `useQueryWrapper: permanently disabled query after ${statusCode}`,
            ),
            {
              extra: {
                queryKey: JSON.stringify(queryKey),
                statusCode,
                errorMessage: error.message,
                url: error.config?.url,
              },
            },
          )
          return false
        }
      }

      return failureCount <= MAX_RETRIES
    },
    initialData,
    placeholderData,
  }) as UseQueryResult<T>
}
